import { useCallback, useMemo, useState } from 'react';
import { Column } from '@/types/common';
import type { ColumnSettingsProps } from '@components/ColumnSettings';

/**
 * useTableColumns
 * ----------------
 * Adds per-user "show / hide / reorder columns" behaviour to any table that
 * renders through the shared `TableData` component.
 *
 * It owns the customization state (which columns are visible and in what
 * order), persists it to `localStorage` keyed by `storageKey`, and hands back:
 *   - `visibleColumns`      → feed straight into `<TableData columns={...} />`
 *   - `columnSettingsProps` → spread into `<ColumnSettings {...} />`
 *
 * Columns flagged `pinned` (and the conventional `actions` column) are always
 * visible, cannot be reordered/hidden, and stay anchored at their defined
 * position. Columns flagged `defaultHidden` start hidden.
 *
 * Persistence is per-browser today. To move to per-user/server-side later,
 * only this hook needs to change — the page-level API stays identical.
 */

const STORAGE_PREFIX = 'fms.tableColumns';

const isPinned = (column: Column): boolean =>
  column.pinned === true || column.key === 'actions';

interface StoredPrefs {
  version: number;
  /** Customizable column keys in user-chosen order. */
  order: string[];
  /** Customizable column keys the user hid. */
  hidden: string[];
}

export interface UseTableColumnsOptions<T> {
  /** Unique, stable id for this table (e.g. 'vehicles-owned'). */
  storageKey: string;
  /** Full column definitions — the source of truth. */
  columns: Column<T>[];
  /**
   * Bump when the column set changes in a way that should reset saved layouts
   * (e.g. a column key was renamed). Defaults to 1.
   */
  version?: number;
}

export interface UseTableColumnsResult<T> {
  /** Ordered + filtered columns to pass to `<TableData>`. */
  visibleColumns: Column<T>[];
  /** Props to spread into `<ColumnSettings>`. */
  columnSettingsProps: ColumnSettingsProps;
}

const storageKeyFor = (storageKey: string) => `${STORAGE_PREFIX}.${storageKey}`;

const readPrefs = (storageKey: string, version: number): StoredPrefs | null => {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(storageKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredPrefs>;
    if (
      !parsed ||
      parsed.version !== version ||
      !Array.isArray(parsed.order) ||
      !Array.isArray(parsed.hidden)
    ) {
      return null;
    }
    return {
      version,
      order: parsed.order.filter((k): k is string => typeof k === 'string'),
      hidden: parsed.hidden.filter((k): k is string => typeof k === 'string'),
    };
  } catch {
    // Corrupt / unavailable storage — fall back to defaults.
    return null;
  }
};

const writePrefs = (storageKey: string, prefs: StoredPrefs): void => {
  try {
    window.localStorage.setItem(
      storageKeyFor(storageKey),
      JSON.stringify(prefs)
    );
  } catch {
    // Storage full / disabled — customization just won't persist.
  }
};

/**
 * Reconcile saved prefs against the current column definitions so the layout
 * survives added / removed / reordered columns in code:
 *   - unknown keys (removed columns) are dropped,
 *   - new columns are appended in their defined order,
 *   - a new column respects its `defaultHidden` flag.
 */
const reconcile = (
  customizableKeys: string[],
  defaultHiddenKeys: Set<string>,
  saved: StoredPrefs | null
): { order: string[]; hidden: Set<string> } => {
  const known = new Set(customizableKeys);

  if (!saved) {
    return {
      order: [...customizableKeys],
      hidden: new Set(defaultHiddenKeys),
    };
  }

  const savedOrder = saved.order.filter((k) => known.has(k));
  const seen = new Set(savedOrder);
  const appended = customizableKeys.filter((k) => !seen.has(k));
  const order = [...savedOrder, ...appended];

  const hidden = new Set(saved.hidden.filter((k) => known.has(k)));
  // Newly added columns that are meant to start hidden.
  appended.forEach((k) => {
    if (defaultHiddenKeys.has(k)) hidden.add(k);
  });

  return { order, hidden };
};

export function useTableColumns<T extends Record<string, any>>({
  storageKey,
  columns,
  version = 1,
}: UseTableColumnsOptions<T>): UseTableColumnsResult<T> {
  // Stable maps derived from the current column definitions.
  const columnByKey = useMemo(() => {
    const map = new Map<string, Column<T>>();
    columns.forEach((col) => map.set(col.key, col));
    return map;
  }, [columns]);

  const customizableKeys = useMemo(
    () => columns.filter((col) => !isPinned(col)).map((col) => col.key),
    [columns]
  );

  const defaultHiddenKeys = useMemo(
    () =>
      new Set(
        columns.filter((col) => col.defaultHidden).map((col) => col.key)
      ),
    [columns]
  );

  // Lazily load + reconcile persisted prefs once.
  const [state, setState] = useState<{ order: string[]; hidden: Set<string> }>(
    () =>
      reconcile(
        customizableKeys,
        defaultHiddenKeys,
        readPrefs(storageKey, version)
      )
  );

  // Keep state in sync if the column definitions themselves change at runtime
  // (e.g. columns that appear/disappear with a tab or feature flag). We only
  // re-reconcile when the *set* of customizable keys changes, so unrelated
  // re-renders never clobber the user's active choices. Adjusting derived
  // state during render is a supported React pattern and converges here.
  const currentKeySet = customizableKeys.slice().sort().join('|');
  const stateKeySet = state.order.slice().sort().join('|');
  if (currentKeySet !== stateKeySet) {
    setState((prev) => {
      const known = new Set(customizableKeys);
      const order = prev.order.filter((k) => known.has(k));
      const present = new Set(order);
      customizableKeys.forEach((k) => {
        if (!present.has(k)) {
          order.push(k);
          if (defaultHiddenKeys.has(k) && !prev.order.includes(k)) {
            prev.hidden.add(k);
          }
        }
      });
      const hidden = new Set(
        Array.from(prev.hidden).filter((k) => known.has(k))
      );
      return { order, hidden };
    });
  }

  const persist = useCallback(
    (order: string[], hidden: Set<string>) => {
      writePrefs(storageKey, {
        version,
        order,
        hidden: Array.from(hidden),
      });
    },
    [storageKey, version]
  );

  const applyChanges = useCallback(
    (order: string[], hiddenKeys: string[]) => {
      const known = new Set(customizableKeys);
      const nextOrder = order.filter((k) => known.has(k));
      // Ensure every customizable key is present exactly once.
      const seen = new Set(nextOrder);
      customizableKeys.forEach((k) => {
        if (!seen.has(k)) nextOrder.push(k);
      });
      const nextHidden = new Set(hiddenKeys.filter((k) => known.has(k)));
      setState({ order: nextOrder, hidden: nextHidden });
      persist(nextOrder, nextHidden);
    },
    [customizableKeys, persist]
  );

  const resetToDefault = useCallback(() => {
    const order = [...customizableKeys];
    const hidden = new Set(defaultHiddenKeys);
    setState({ order, hidden });
    persist(order, hidden);
  }, [customizableKeys, defaultHiddenKeys, persist]);

  // Build the ordered, visible column list. Pinned columns stay anchored at
  // their original index; customizable columns fill the remaining slots in the
  // user's chosen order, with hidden ones removed.
  const visibleColumns = useMemo(() => {
    const orderedVisibleCustom = state.order
      .filter((k) => !state.hidden.has(k))
      .map((k) => columnByKey.get(k))
      .filter((c): c is Column<T> => Boolean(c));

    const result: Column<T>[] = [];
    let ci = 0;
    columns.forEach((col) => {
      if (isPinned(col)) {
        result.push(col);
      } else if (ci < orderedVisibleCustom.length) {
        result.push(orderedVisibleCustom[ci]);
        ci += 1;
      }
    });
    while (ci < orderedVisibleCustom.length) {
      result.push(orderedVisibleCustom[ci]);
      ci += 1;
    }
    return result;
  }, [columns, columnByKey, state.order, state.hidden]);

  const columnSettingsProps = useMemo<ColumnSettingsProps>(() => {
    const items = state.order
      .map((k) => columnByKey.get(k))
      .filter((c): c is Column<T> => Boolean(c))
      .map((col) => ({ key: col.key, title: col.title || col.key }));

    return {
      items,
      hiddenKeys: Array.from(state.hidden),
      onApply: applyChanges,
      onReset: resetToDefault,
    };
  }, [state.order, state.hidden, columnByKey, applyChanges, resetToDefault]);

  return { visibleColumns, columnSettingsProps };
}

export default useTableColumns;
